import type { UseQueryOptions } from '@zg-rentals/react-query5';
import { useQuery } from '@zg-rentals/react-query5';

export type UseQueryOverrides<T> = Omit<UseQueryOptions<T>, 'queryKey' | 'queryFn'>;

export type RedirectHref = string | ((href: string, params: URLSearchParams) => string);

/**
 * Create an authentication Provider/Boundary. See Confluence for details:
 * https://zillowgroup.atlassian.net/wiki/spaces/RP/pages/959381921/auth-react
 */
export function makeAuthProvider<Props extends object, User>({
  fetchUser,
  authorizeUser = () => true,

  queryKey = ['modules', 'auth-react', 'makeAuthProvider'],
  providerQueryOptions,

  searchParam: [searchParamKey, searchParamValue] = ['ap', 'x'],
}: {
  /**
   * Fetch user, likely from remote endpoint or API
   */
  fetchUser: (props: Props) => Promise<User>;

  /**
   * Determine if user is authorized (after the user is fetched). Default
   * behavior is to always return true.
   */
  authorizeUser?: (user: User, props: Props) => boolean;

  /**
   * Provide a custom react-query queryKey, useful if you use multiple Auth Providers
   */
  queryKey?: Array<string>;

  /**
   * Provide optional react-query overrides, perhaps to specify staleTime or refetchIntervals
   */
  providerQueryOptions?: UseQueryOverrides<User>;

  /**
   * Optional: search param to set
   */
  searchParam?: [key: string, value: string];
}) {
  const queryOptions = (props: Props, queryOptions?: UseQueryOverrides<User>): UseQueryOptions<User> => ({
    // Re-fetch auth results after 3 minutes (can be overridden by host)
    refetchInterval: 3 * 60 * 1000,

    // Useful for de-duplicating queries when several components call (even implicitly) `useQuery`
    // around the same time,
    staleTime: 30 * 1000,

    // Expect underlying functions to handle retries
    retry: false,

    // Defer to caller to override above settings with either `makeAuthProvider` overrides,
    // or prop-based overrides
    ...providerQueryOptions,
    ...queryOptions,

    // Note: not override-able!
    queryKey,
    queryFn: () => fetchUser(props),
  });

  // "Context" to propagate from Provider down to Boundary or callers of useAuth
  let useQueryOptions: ReturnType<typeof queryOptions>, authorizeUserProps: Props;

  // Note: useful so children don't need to provide Provider props directly to `useQuery(query(props))`
  const useAuth = () => {
    try {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const { data: user = null, error, refetch } = useQuery(useQueryOptions);

      return {
        user,
        isAuthorized: user ? authorizeUser(user, authorizeUserProps) : false,
        error,
        refetch: async () => {
          await refetch();
        },
      };
    } catch (_) {
      return { user: null, isAuthorized: false, error: null, refetch: async () => {} };
    }
  };

  const Provider = (
    props: {
      /**
       * Rendered unconditionally, to prevent components from rendering if user is unauthorized
       * use Boundary component
       */
      children: React.ReactNode;

      queryOptions?: UseQueryOverrides<User>;
    } & Props,
  ) => {
    // Note: calculated _just once_ when Provider is rendered, and never changes!
    useQueryOptions ??= queryOptions(props, props.queryOptions);
    authorizeUserProps ??= props;

    // Initiate react-query query
    useQuery(useQueryOptions);

    return props.children;
  };

  const Boundary = ({
    children,
    loading = <>Loading...</>,
    redirectHref,
    onError,
    onUnauthorized,
  }: {
    /**
     * Rendered when user is authorized (using provider logic)
     */
    children: React.ReactNode | ((user: User) => React.ReactNode);

    /**
     * Rendered while user is being fetched/authorized
     */
    loading?: React.ReactNode;

    /**
     * Produces a redirect `onUnauthorized`, use `params` to break redirect loops
     */
    redirectHref?: RedirectHref;

    /**
     * Rendered if there was an error fetching or authorizing user
     */
    onError?: React.ReactNode | ((reason: string) => React.ReactNode);

    /**
     * Rendered if user is still unauthorized after a redirect
     */
    onUnauthorized?: React.ReactNode | ((user: User) => React.ReactNode);
  }) => {
    const handleError = (message: string) => (typeof onError === 'function' ? onError(message) : (onError ?? message));

    const handleUnauthorized = (unauthorizedUser: User) => {
      if (redirectHref) {
        const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');

        if (params.get(searchParamKey) !== searchParamValue) {
          params.set(searchParamKey, searchParamValue);

          if (typeof window !== 'undefined') {
            window.location.href =
              typeof redirectHref === 'string'
                ? redirectHref
                : redirectHref(window.location.href.split('?')[0], params);
            return <>Redirecting...</>;
          } else if (typeof redirectHref === 'function') {
            redirectHref('', params);
            return null;
          }
        }
      }

      return typeof onUnauthorized === 'function'
        ? onUnauthorized(unauthorizedUser)
        : (onUnauthorized ?? handleError('User not authorized'));
    };

    const { error, user, isAuthorized } = useAuth();

    if (error) {
      return handleError(error instanceof Error ? error.message : 'Unknown error');
    } else if (user != null) {
      if (isAuthorized === false) {
        return handleUnauthorized(user);
      } else {
        return typeof children === 'function' ? children(user) : children;
      }
    } else {
      return loading;
    }
  };

  return {
    /**
     * Wrap around entire App to provide auth context to children
     */
    Provider,

    /**
     * Wrap around a React sub-tree to ensure that user is logged in and
     * authorized before rendering children
     */
    Boundary,

    /**
     * Access underlying user/authorized state directly
     */
    useAuth,

    /**
     * Function for generating useQuery options, useful for SSR
     */
    queryOptions,

    /**
     * Helper for authorizing user during SSR
     */
    authorizeUser,
  };
}
